import { useEffect, useState } from "react";

import Bars2Icon from "@heroicons/react/24/outline/Bars2Icon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import { Link, useNavigate } from "@remix-run/react";
import { motion } from "framer-motion";
import { Link as ScrollLink, scroller as scroll } from "react-scroll";

import KonnectButton from "~/components/common/KonnectButton/KonnectButton";
import { Logo } from "~/components/common/Logo/Logo";
import { APP_PAGE_URL, LOGIN_URL } from "~/Config/config";
import { tw } from "~/utils";

import type { Navigation } from "../Sections/util/map-navigation.server";

interface HeaderProps {
  navItems: Navigation[];
  showAppButton?: boolean;
  isHome?: boolean;
  showBanner?: boolean;
}

const TOGGLE_MENU_DISTANCE = 100;

const classNameLink =
  "lg:text-xs lg:text-sm font-semibold uppercase transition hover:text-purple cursor-pointer";

export const SCROLL_OFFSET = 200;

const Header = ({
  navItems,
  showAppButton,
  isHome = false,
  showBanner = true,
}: HeaderProps) => {
  // False when menu is at the top
  const [isOpaque, setIsOpaque] = useState(isHome ? false : true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMenuOpen = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    if (isMenuOpen) {
      setIsOpaque(true);
      document.body.style.overflow = "hidden";
    } else {
      window.pageYOffset < TOGGLE_MENU_DISTANCE && isHome && setIsOpaque(false);
      document.body.style.overflow = "auto";
    }
  }, [isMenuOpen, isHome]);

  // Set isSticky to true if the page is scrolled to the top
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.pageYOffset;
      if (scrollPos > TOGGLE_MENU_DISTANCE) {
        setIsOpaque(true);
      } else {
        isHome && setIsOpaque(false);
      }
    };

    if (window.pageYOffset > TOGGLE_MENU_DISTANCE) {
      setIsOpaque(true);
    }

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isHome]);

  const buttonClasses = `hidden rounded-full border-[1px] px-[15px] py-[10px] text-white transition lg:inline-flex border-transparent bg-purple hover:bg-purple/80`;

  return (
    <header
      className={`sticky top-0 z-50 w-full ${
        isMenuOpen ? "h-screen" : "h-auto"
      }`}
    >
      {showBanner && (
        <div
          className={`border-b-[0.5px] border-lightGrey bg-teal px-[30px] py-[15px] text-center text-xs transition lg:text-sm`}
        >
          <span>
            Hey there! Free trials are available for all Konnect plans.
          </span>
          <ScrollLink
            to={"PricingSection"}
            className="cursor-pointer underline"
            smooth={true}
            offset={-SCROLL_OFFSET}
            duration={500}
          >
            Start for free today.
          </ScrollLink>
        </div>
      )}
      <div
        className={`section-padding-x flex items-center justify-between border-b-2 ${
          isOpaque ? "border-lightGrey" : "border-offWhite"
        } bg-offWhite py-[20px] transition`}
      >
        <Link to={"/"}>
          <Logo color="charcoal" className={`w-[120px] lg:w-[180px]`} />
        </Link>
        <nav className={`hidden lg:flex lg:gap-4`}>
          {navItems.map((navItem) =>
            isHome ? (
              <ScrollLink
                key={navItem.title}
                className={classNameLink}
                to={navItem.url}
                smooth={true}
                offset={-SCROLL_OFFSET}
                duration={500}
              >
                {navItem.title}
              </ScrollLink>
            ) : (
              <Link
                className={classNameLink}
                to={`/#${navItem.url}`}
                key={navItem.title}
              >
                {navItem.title}
              </Link>
            )
          )}
        </nav>
        <div className="flex items-center gap-[30px]">
          {showAppButton ? (
            <Link to={`${APP_PAGE_URL}`} prefetch="intent" replace>
              <KonnectButton
                text={"Go to app"}
                icon={true}
                className={tw(buttonClasses, "inline-flex")}
              />
            </Link>
          ) : (
            <>
              <KonnectButton
                text={"Start free trial"}
                onClick={() => {
                  if (isHome) {
                    scroll.scrollTo("PricingSection", {
                      duration: 500,
                      smooth: true,
                      offset: -SCROLL_OFFSET,
                    });
                  } else {
                    navigate("/#PricingSection");
                  }
                }}
                className="hidden lg:inline-flex"
              />
              <Link to={LOGIN_URL}>
                <span className={classNameLink}>Login</span>
              </Link>
            </>
          )}
          <button className="size-[35px] lg:hidden" onClick={toggleMenuOpen}>
            {isMenuOpen ? (
              <XMarkIcon className="text-charcoal" />
            ) : (
              <Bars2Icon className="text-charcoal" />
            )}
          </button>
        </div>
      </div>
      {isMenuOpen && (
        <motion.nav
          initial={{ opacity: 0, height: 0, backgroundColor: "transparent" }}
          transition={{ duration: 0.3, delay: 0.2 }}
          animate={{ opacity: 1, height: "100%", backgroundColor: "#161616" }}
          className={`absolute flex w-full flex-col items-center gap-3 overflow-hidden pt-[20vh] lg:hidden`}
        >
          {navItems.map((navItem) => (
            <motion.button
              key={navItem.title}
              className={"text-xl font-semibold uppercase text-offWhite"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.6 }}
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                isHome
                  ? scroll.scrollTo(navItem.url, {
                      duration: 500,
                      smooth: true,
                      offset: -900,
                      delay: 100,
                    })
                  : navigate(`/#${navItem.url}`);
              }}
            >
              {navItem.title}
            </motion.button>
          ))}
        </motion.nav>
      )}
    </header>
  );
};

export default Header;
